"use client";

import { routing } from "@/i18n/routing";
import { missingTranslationsDetector } from "@/utils/missingTranslationsDetector";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";

// Domain kontrolünü component dışında yap (sadece bir kez hesaplanır)
const checkAllowedDomain = () => {
  if (typeof window === "undefined") return false;

  const allowedDomains = [
    "http://localhost:3000",
    "https://stage.turna.com",
    "https://test.turna.com",
    "https://cce-saka-test.turna.com",
  ];

  return allowedDomains.includes(window.location.origin);
};

const MissingTranslationsFloatingButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [report, setReport] = useState(null);
  const [copied, setCopied] = useState(false);
  const [translationsDraft, setTranslationsDraft] = useState({});

  const locales = routing.locales;

  // useMemo ile sadece bir kez hesapla
  const allowLocalizationDomain = useMemo(() => checkAllowedDomain(), []);

  useEffect(() => {
    if (!allowLocalizationDomain) return;

    const interval = setInterval(() => {
      const currentReport = missingTranslationsDetector.getReport();
      if (currentReport.totalMissing > 0) {
        setReport(currentReport);
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [allowLocalizationDomain]);

  const handleReset = () => {
    missingTranslationsDetector.reset();
    setIsVisible(false);
    setIsExpanded(false);
    setIsEditMode(false);
    setTranslationsDraft({});
  };

  const handleExportTxt = () => {
    if (!report) return;
    const keyList = report.allMissingKeys.map((i) => i.key).join("\n");
    const blob = new Blob([keyList], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "missing-keys.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    if (!report) return;
    try {
      await navigator.clipboard.writeText(
        report.allMissingKeys.map((i) => i.key).join("\n"),
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const handleAddMissingKeys = () => {
    if (!report) return;

    const validKeys = report.allMissingKeys.filter(
      (item) =>
        item.key && typeof item.key === "string" && item.key.trim() !== "",
    );

    setTranslationsDraft((prev) => {
      const next = { ...prev };
      validKeys.forEach(({ key }) => {
        next[key] = next[key] ?? {};
        locales.forEach((locale) => {
          if (next[key][locale] === undefined) {
            next[key][locale] = "";
          }
        });
      });
      return next;
    });
    setIsEditMode(true);
  };

  const displayMissingKeys = useMemo(() => {
    if (!report?.allMissingKeys) return [];
    return report.allMissingKeys.filter(
      (item) =>
        item.key && typeof item.key === "string" && item.key.trim() !== "",
    );
  }, [report?.allMissingKeys]);

  const handleCsvExport = () => {
    if (!translationsDraft || !Object.keys(translationsDraft).length) return;

    // Sadece en az bir dilde içerik girilmiş key'leri export et
    const hasAnyContent = (values) =>
      locales.some(
        (locale) =>
          values[locale] != null && String(values[locale]).trim() !== "",
      );

    // Header
    const headers = ["namespace", "key", ...locales];

    const rows = Object.entries(translationsDraft)
      .filter(
        ([fullKey, values]) =>
          fullKey &&
          fullKey.trim() !== "" &&
          values &&
          hasAnyContent(values),
      )
      .map(([fullKey, values]) => {
        const [namespace, ...keyParts] = fullKey.split(".");
        const key = keyParts.join("."); // nested key'ler korunur

        return [
          namespace || "",
          key || "",
          ...locales.map(
            (locale) => `"${(values[locale] || "").replace(/"/g, '""')}"`,
          ),
        ].join(",");
      });

    if (!rows.length) return;

    const csvContent = [headers.join(","), ...rows].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "missing-translations.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!allowLocalizationDomain || !isVisible) return null;

  if (!isExpanded) {
    return (
      <div className="fixed right-25 bottom-6 z-50 hidden md:block">
        <button
          onClick={() => setIsExpanded(true)}
          className="relative flex h-12 w-12 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg"
        >
          🔍
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-400 font-semibold text-black">
            {report?.totalMissing}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "fixed z-50 hidden overflow-hidden rounded-lg md:block",
        isEditMode
          ? "inset-[5%] shadow-[0px_0_0_99px_rgba(0,0,0,0.62)]"
          : "right-4 bottom-4 w-180",
      )}
    >
      <div
        className={clsx(
          "rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 p-5 text-white shadow-2xl",
          isEditMode && "h-full",
        )}
      >
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isEditMode && (
              <button
                onClick={() => setIsEditMode(false)}
                className="cursor-pointer rounded-md bg-white/10 px-2 py-1 hover:bg-white/20"
              >
                ← Listeye Dön
              </button>
            )}

            <div>
              <h3 className="font-semibold">
                Eksik Çeviriler {isEditMode && "(Edit Mode)"}
              </h3>
              <p className="text-slate-300">{report.totalMissing} adet</p>
            </div>
          </div>

          <button
            onClick={() => {
              setIsExpanded(false);
              setIsEditMode(false);
            }}
            className="cursor-pointer rounded-lg p-1.5 hover:bg-white/10"
          >
            <FontAwesomeIcon icon={byPrefixAndName.fal["xmark"]} />
          </button>
        </div>

        {/* Table */}
        <div
          className={clsx(
            "scrollbar-default overflow-auto rounded-lg bg-black/20",
            isEditMode ? "h-[85%]" : "max-h-80",
          )}
        >
          <table className="w-full">
            <thead className="sticky top-0 bg-slate-700">
              <tr>
                <th className="px-3 py-2 text-left">Namespace</th>
                <th className="px-3 py-2 text-left">Key</th>
                {isEditMode &&
                  locales.map((l) => (
                    <th key={l} className="px-3 py-2 uppercase">
                      {l}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {displayMissingKeys.map(({ key }, idx) => {
                const [ns, ...rest] = key.split(".");
                return (
                  <tr key={key || idx} className="border-b border-white/5">
                    <td className="px-3 py-2 font-medium">{ns || "—"}</td>
                    <td className="px-3 py-2">{rest.join(".") || "—"}</td>

                    {isEditMode &&
                      locales.map((locale) => (
                        <td key={locale} className="px-2 py-2">
                          <textarea
                            className="min-h-[60px] w-full rounded bg-black/30 p-2"
                            value={translationsDraft[key]?.[locale] || ""}
                            onChange={(e) =>
                              setTranslationsDraft((prev) => ({
                                ...prev,
                                [key]: {
                                  ...prev[key],
                                  [locale]: e.target.value,
                                },
                              }))
                            }
                          />
                        </td>
                      ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Actions */}
        <div
          className={clsx(
            "mt-4 grid gap-2",
            isEditMode ? "grid-cols-5" : "grid-cols-4",
          )}
        >
          <button
            onClick={handleAddMissingKeys}
            className="flex cursor-pointer items-center justify-center gap-2 rounded bg-blue-600 px-3 py-2"
          >
            <FontAwesomeIcon icon={byPrefixAndName.fal["plus"]} />
            <span>Eksikleri Ekle</span>
          </button>
          {isEditMode && (
            <button
              onClick={handleCsvExport}
              className="flex cursor-pointer items-center justify-center gap-2 rounded bg-purple-600 px-3 py-2"
            >
              <FontAwesomeIcon icon={byPrefixAndName.fal["file-csv"]} />
              <span>CSV Export</span>
            </button>
          )}
          <button
            onClick={handleExportTxt}
            className="flex cursor-pointer items-center justify-center gap-2 rounded bg-emerald-600 px-3 py-2"
          >
            <FontAwesomeIcon icon={byPrefixAndName.fal["file"]} />
            <span>TXT</span>
          </button>

          <button
            onClick={handleCopy}
            className="flex cursor-pointer items-center justify-center gap-2 rounded bg-amber-600 px-3 py-2"
          >
            <FontAwesomeIcon icon={byPrefixAndName.fal["copy"]} />
            <span>{copied ? "✓" : "Kopyala"}</span>
          </button>

          <button
            onClick={handleReset}
            className="flex cursor-pointer items-center justify-center gap-2 rounded bg-slate-600 px-3 py-2"
          >
            <FontAwesomeIcon icon={byPrefixAndName.fal["xmark"]} />
            <span>Gizle</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export const TranslationDetectorProvider = ({ children }) => {
  // Domain kontrolünü bu component içinde de yap
  const allowLocalizationDomain = useMemo(() => checkAllowedDomain(), []);

  useEffect(() => {
    if (allowLocalizationDomain) {
      window.missingTranslationsDetector = missingTranslationsDetector;
    }
  }, [allowLocalizationDomain]);

  return (
    <>
      {children}
      <MissingTranslationsFloatingButton />
    </>
  );
};
